import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import type { QuickPostModalProps } from "@circle-react/components/QuickPost/types";
import { resetPersistedDraft } from "@circle-react/components/QuickPostV3/data/usePersistedDraft";
import { useModalClose } from "@circle-react/components/QuickPostV3/hooks/useModalClose";
import { LegacyEditorPortalLayeringStyles } from "@circle-react/components/QuickPostV3/LegacyEditorPortalLayeringStyles";
import { NewPostWrapper } from "@circle-react/components/QuickPostV3/NewPostWrapper";
import { useQuickPostV3ModalStore } from "@circle-react/components/QuickPostV3/store";
import { PostsDetailsContextProvider } from "@circle-react/contexts/postsDetailsContext";
import { SpacesContextProvider } from "@circle-react/contexts/spacesContext";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { Modal } from "@circleco/compass/components/Modal";
import { useCallback, useEffect, useState } from "react";

const QuickPostV3ModalComponent = ({
  onSubmitSuccess,
  shouldSkipRedirectAfterSubmit,
  spaceId,
  tiptapBodyForEditor,
  title,
}: QuickPostModalProps) => {
  const [shouldPersistDraft, setShouldPersistDraft] = useState(true);
  const handleDiscardChanges = useCallback(() => {
    setShouldPersistDraft(false);
    resetPersistedDraft();
  }, []);
  const modal = useModal();
  const { closeModal, forceClose, handleOpenChange, isOpen } = useModalClose({
    onDiscardChanges: handleDiscardChanges,
  });
  const reset = useQuickPostV3ModalStore(state => state.reset);
  const isFullscreen = useQuickPostV3ModalStore(state => state.isFullscreen);
  const isSmScreen = useSmScreenMediaQuery();
  const modalSize = isFullscreen || isSmScreen ? "full-screen" : "lg";

  useEffect(() => {
    if (modal.visible) {
      reset();
      setShouldPersistDraft(true);
    }
  }, [modal.visible, reset]);

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange} size={modalSize}>
      <LegacyEditorPortalLayeringStyles active={modal.visible} />
      <SpacesContextProvider>
        <PostsDetailsContextProvider>
          <NewPostWrapper
            onForceClose={forceClose}
            onRequestClose={closeModal}
            shouldPersistDraft={shouldPersistDraft}
            spaceId={spaceId}
            tiptapBodyForEditor={tiptapBodyForEditor}
            title={title}
            onSubmitSuccess={onSubmitSuccess}
            shouldSkipRedirectAfterSubmit={shouldSkipRedirectAfterSubmit}
          />
        </PostsDetailsContextProvider>
      </SpacesContextProvider>
    </Modal.Root>
  );
};

export const QuickPostV3Modal = ModalManager.create(
  (props: QuickPostModalProps) => <QuickPostV3ModalComponent {...props} />,
);
